import { map, Observable, tap } from 'rxjs';
import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  API_BASEURL, APIHelper,
  DataGetOptions,
  DataGetRequest,
  ItemRecord, TextHelper
} from '@controladad/ng-base';
import { GetApiAdapter, GetApiAdapterOptionsToQueryParam, GetApiRequest } from '../adapters';

type ResponseType = 'json' | 'text' | 'blob' | undefined;
interface ApiOptions {
  responseType?: ResponseType;
}

export type EndpointMethods = 'get' | 'getById' | 'post' | 'put' | 'delete' | 'deleteById';
export type EndpointMapper = { [p in EndpointMethods]?: ((path: string, prefix?: string) => string) | string };

class Endpoint {
  endpoints!: { [p in EndpointMethods]: string };

  constructor(
    public opts: {
      path: string;
      prefix?: string;
      mapper?: EndpointMapper;
    },
  ) {
    const path = TextHelper.toPascalCase(opts.path);
    let prefix = APIHelper.dropSlash(TextHelper.toPascalCase(this.opts.prefix));
    if (prefix.startsWith('/')) {
      prefix = prefix.slice(1);
    }

    const defaultMapper: EndpointMapper = {
      get: `${prefix}/Get${path}List`,
      getById: `${prefix}/Get${path}Details`,
      post: `${prefix}/Add${path}`,
      put: `${prefix}/Edit${path}`,
      delete: `${prefix}/Delete${path}`,
      deleteById: `${prefix}/Delete${path}`,
    };
    this.endpoints = {
      ...defaultMapper,
      ...(this.opts.mapper
        ? Object.entries(this.opts.mapper).reduce((pre, [key, value]) => {
          pre[key] = typeof value === 'function' ? value(path) : value;
          return pre;
        }, {} as any)
        : {}),
    };
  }

  get get() {
    return this.endpoints.get;
  }
  get getById() {
    return this.endpoints.getById;
  }
  get post() {
    return this.endpoints.post;
  }
  get put() {
    return this.endpoints.put;
  }
  get deleteById() {
    return this.endpoints.deleteById;
  }
  get delete() {
    return this.endpoints.delete;
  }
}

export class BaseApi<ENTITY, CREATE = any, UPDATE = any> {
  readonly http = inject(HttpClient);
  readonly apiBaseUrl = inject(API_BASEURL);

  protected endpoint: Endpoint;
  protected prefix: string;

  /**
   *
   * @param endpoint is the base url to request at, slash at the start is optional
   * @param endpointMapper is used when the endpoint path is not following the default rule and needs to be changed.
   */
  constructor(endpoint: string, endpointMapper?: EndpointMapper) {
    const segments = APIHelper.dropSlash(endpoint).split('/');
    this.prefix = `/${segments.length > 1 ? segments.slice(0, -1).join('/') + '/' : ''}`;
    this.endpoint = new Endpoint({
      path: segments.at(-1) ?? '',
      prefix: this.prefix,
      mapper: endpointMapper,
    });
  }

  getAdapter(options?: DataGetOptions<ENTITY>) {
    try {

      return GetApiAdapter<ENTITY>(`${this.endpoint.get}`, options);
    } catch (error) {
      console.log(error);
      throw error;

    }
  }

  getAdapterFiltered(defaultFilters?: DataGetRequest['filters']) {
    return this.getAdapter({
      default: {
        filters: defaultFilters,
      },
    });
  }

  get(id: string | number, opts?: ApiOptions): Observable<ENTITY> {
    return this.http.get(`${this.endpoint.getById}/${id}`, this.getOptions(opts) as never).pipe(
      map((result: any) => {
        const keys = Object.keys(result);
        if (keys.length === 1 && !keys.includes('id')) {
          return result[keys[0]];
        }
        return result;
      }),
    ) as never;
  }

  getAll<T = ENTITY>(opts?: DataGetRequest, overrideUrl?: string): Observable<T[]> {
    return GetApiRequest<T>(overrideUrl ?? `${this.endpoint.get}`, {
      pagination: {
        page: 1,
        size: 999999,
      },
      ...opts,
    });
  }

  create<T>(model: CREATE, opts?: ApiOptions): Observable<T> {
    return this.http.post<T>(`${this.endpoint.post}`, model, this.getOptions(opts) as never) as never;
  }

  update<T>(id: number, model: UPDATE, opts?: ApiOptions): Observable<T> {
    return this.http.put<T>(`${this.endpoint.put}/${id}`, model, this.getOptions(opts) as never) as never;
  }

  delete<T>(id: number | number[] | { id: number } | { id: number }[], opts?: ApiOptions): Observable<T> {
    return id instanceof Array
      ? (this.http.delete(
        `${this.prefix}${this.endpoint.delete}?${id.map((x) => `ID=${typeof x === 'number' ? x : x.id}`).join('&')}`,
        this.getOptions(opts) as never,
      ) as never)
      : (this.http.delete(
        `${this.prefix}${this.endpoint.deleteById}/${typeof id === 'number' ? id : id.id}`,
        this.getOptions(opts) as never,
      ) as never);
  }

  protected getItemRecords(
    mapTo: keyof ENTITY | ((item: ENTITY) => string | ItemRecord<number>),
    filterByStatus = true,
    filterBy?: ((item: ENTITY) => boolean) | DataGetRequest['filters'],
  ): Observable<ItemRecord<number>[]> {
    const filters: DataGetRequest['filters'] = [];
    if (filterByStatus) {
      filters.push({
        key: 'status',
        value: 'active',
      });
    }
    if (filterBy instanceof Array) {
      filters.push(...filterBy);
    }

    return this.getAll({
      filters,
    }).pipe(
      map((result) => {
        let currentList = result;
        // We also do the filter manually, cause not all endpoints support filters...
        if (filterByStatus) {
          currentList = currentList.filter((t) => {
            if (!t || typeof t !== 'object') return true;
            let status: string | boolean | undefined;
            if ('status' in t) {
              status = t.status as any;
            }

            if (status !== undefined) {
              return typeof status === 'string' ? status.toLowerCase() === 'active' : status;
            }

            return true;
          });
        }
        if (filterBy && typeof filterBy === 'function') {
          currentList = currentList.filter(filterBy);
        }
        return currentList.map((t) => {
          if (typeof mapTo === 'function') {
            const fnResult = mapTo(t);
            return typeof fnResult === 'string' ? { label: fnResult, value: t['id' as never] } : fnResult;
          } else {
            return { label: t[mapTo], value: t['id' as never] };
          }
        }) as ItemRecord<number>[];
      }),
    );
  }

  protected exportAndDownload(url: string, query?: DataGetRequest, filename?: string) {
    const params = GetApiAdapterOptionsToQueryParam({
      ...query,
      pagination: undefined,
    });
    return this.http
      .get(`${APIHelper.prependSlash(url)}?${params}`, { responseType: 'blob' })
      .pipe(tap((blob) => this.downloadBlob(blob, filename)));
  }

  public downloadBlob(blob: Blob, filename?: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename ? filename : 'export'}`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private getOptions(options?: ApiOptions | undefined) {
    return {
      responseType: 'json' as const,
      ...(options ?? {}),
    };
  }
}
